import React from 'react';
import { message, Popconfirm, Spin, Tooltip, Upload } from 'antd';
import { DeleteOutlined, FileOutlined, InboxOutlined } from '@ant-design/icons';
import apiClient from '../../../utils/apiService';
import { formatDisplayDate } from '../../../utils/dateDisplay';
import RemarkInlineEditor from './RemarkInlineEditor';

const ATTACHMENT_TIME_FORMAT = 'MM/DD/YY HH:mm:ss';

const FilesUploadPanel = React.memo(function FilesUploadPanel({
  hblNo,
  uploadedFiles,
  uploadedFilesLoading,
  uploadedFilesError,
  selectedAttachment,
  setSelectedAttachment,
  getFileIcon,
  renderAttachmentPreview,
  editingRemarkId,
  editingRemarkType,
  setEditingRemarkId,
  setEditingRemarkType,
  savingRemark,
  handleUpdateRemark,
  handleDeleteAttachment,
}) {
  const [uploading, setUploading] = React.useState(false);
  const [files, setFiles] = React.useState(uploadedFiles);
  const [filesLoading, setFilesLoading] = React.useState(uploadedFilesLoading);
  const [filesError, setFilesError] = React.useState(uploadedFilesError);
  const [messageApi, contextHolder] = message.useMessage();

  React.useEffect(() => {
    setFiles(uploadedFiles);
    setFilesLoading(uploadedFilesLoading);
    setFilesError(uploadedFilesError);
  }, [uploadedFiles, uploadedFilesLoading, uploadedFilesError]);

  const refreshFiles = React.useCallback(async () => {
    try {
      if (!hblNo) return;
      const res = await apiClient.get(`/shipping-order/attachments/${hblNo}`);
      const { data } = res?.data || {};
      const list = Array.isArray(data) ? data : [];
      setFiles(list);
    } catch (e) {
      console.error('refresh files failed', e);
    }
  }, [hblNo]);

  const handleUpload = React.useCallback(async ({ file, onSuccess, onError }) => {
    try {
      setUploading(true);
      messageApi.success('Uploading, Please Close to Wait...');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('hblNo', hblNo);

      const response = await apiClient.post('/shipping-order/upload-attachment', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      messageApi.success(`${file.name} uploaded successfully`);
      onSuccess(response.data);
      await refreshFiles();
    } catch (error) {
      console.error('Upload error:', error);
      onError(error);
    } finally {
      setUploading(false);
    }
  }, [hblNo, messageApi, refreshFiles]);

  const handleDelete = React.useCallback(async (item) => {
    await handleDeleteAttachment(item);
    await refreshFiles();
  }, [handleDeleteAttachment, refreshFiles]);

  const handleSaveRemark = React.useCallback(async (itemId, remark, type) => {
    const success = await handleUpdateRemark(itemId, remark, type);
    if (success !== false) {
      setFiles(prev => prev.map(item =>
        (item.id === itemId || item.attachment_id === itemId) ? { ...item, remark } : item
      ));
    }
  }, [handleUpdateRemark]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {contextHolder}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', gap: '16px', minHeight: 0 }}>
        <div style={{ width: '30%', minWidth: '300px', display: 'flex', flexDirection: 'column', gap: '16px', minHeight: 0 }}>
          <Upload.Dragger
            name="file"
            multiple
            customRequest={handleUpload}
            showUploadList={false}
            disabled={uploading}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Click or drag file to this area to upload</p>
            <p className="ant-upload-hint">
              Support for single or bulk upload. Files will be associated with HBL No: {hblNo}
            </p>
          </Upload.Dragger>

          <div style={{ flex: 1, minHeight: 0, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: '4px', backgroundColor: '#fff' }}>
            {uploading || filesLoading ? (
              <div className="email-loading" style={{ padding: '20px' }}>
                <Spin tip={uploading ? 'Uploading...' : 'Loading...'} size="small" />
              </div>
            ) : filesError ? (
              <div className="email-empty">{filesError}</div>
            ) : files.length === 0 ? (
              <div className="email-empty">No files uploaded yet</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', padding: '8px' }}>
                {files.map((item, index) => {
                  const itemId = item.id || item.attachment_id || index;
                  const isSelected = selectedAttachment &&
                    ((item.id && selectedAttachment.id === item.id) ||
                      (item.attachment_id && selectedAttachment.attachment_id === item.attachment_id));
                  return (
                    <div
                      key={itemId}
                      onClick={() => setSelectedAttachment(item)}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                        padding: '12px',
                        cursor: 'pointer',
                        border: isSelected ? '1px solid #91d5ff' : '1px solid #f0f0f0',
                        borderLeft: isSelected ? '3px solid #1890ff' : '3px solid transparent',
                        borderRadius: '8px',
                        backgroundColor: isSelected ? '#e6f7ff' : '#fff',
                        marginBottom: '8px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', paddingTop: 2 }}>
                          {getFileIcon(item.fileName)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Tooltip title={item.fileName}>
                            <div style={{ fontSize: '13px', fontWeight: 500, lineHeight: 1.5, wordBreak: 'break-word' }}>
                              {item.fileName}
                            </div>
                          </Tooltip>
                          <div style={{ marginTop: '6px', fontSize: '12px', color: '#999', display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <span>Uploaded by: {item.createBy || '--'}</span>
                            <span>{item.createTime ? formatDisplayDate(item.createTime, ATTACHMENT_TIME_FORMAT) : '--'}</span>
                          </div>
                        </div>
                      </div>
                      <div
                        style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6, flexShrink: 0 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {!!(item.id || item.attachment_id) && (
                          <Popconfirm
                            title="Delete Attachment"
                            description={`Are you sure you want to delete "${item?.fileName || item?.file_name || 'this file'}"?`}
                            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
                            onConfirm={() => handleDelete(item)}
                            placement="topRight"
                            getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
                          >
                            <Tooltip title="Delete attachment">
                              <div style={{
                                width: '22px', height: '22px', borderRadius: '50%',
                                backgroundColor: '#fff1f0', border: '1px solid #ffccc7',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                              }}><DeleteOutlined style={{ fontSize: '11px', color: '#ff4d4f' }} /></div>
                            </Tooltip>
                          </Popconfirm>
                        )}
                      </div>
                      <RemarkInlineEditor
                        itemId={itemId}
                        type="file"
                        remark={item.remark}
                        editingRemarkId={editingRemarkId}
                        editingRemarkType={editingRemarkType}
                        setEditingRemarkId={setEditingRemarkId}
                        setEditingRemarkType={setEditingRemarkType}
                        savingRemark={savingRemark}
                        onSave={handleSaveRemark}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div style={{ width: '70%', minWidth: '400px', border: '1px solid #d9d9d9', borderRadius: '4px', backgroundColor: '#fafafa', minHeight: 0 }}>
          {selectedAttachment ? (
            <div className="attachment-preview-content" style={{ height: '100%', overflow: 'auto' }}>
              {renderAttachmentPreview()}
            </div>
          ) : (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '14px' }}>
              <div style={{ textAlign: 'center' }}>
                <FileOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                <p>Select a file to preview</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default FilesUploadPanel;
